<template>
  <div class="ducks-collection w-full">
    <!-- Summary stats with enhanced animations -->
    <div class="stats-section text-center mb-6">
      <h3 class="text-xl sm:text-2xl font-bold text-slate-800  mb-3 title-animation">
        Duck Hunt Progress
      </h3>
      
      <!-- Make stats row scroll horizontally on very small screens instead of wrapping -->
      <div class="flex justify-center items-center gap-2 mb-4 stats-container stats-fade-in">
        <div class="stat-card bg-white/60  px-3 sm:px-4 py-2 rounded-lg shadow-sm stats-item" style="--order: 1">
          <p class="text-xs text-gray-500 ">Total</p>
          <p class="font-bold text-lg text-slate-800  animate-count">{{ getTotalDucks }}</p>
        </div>
        <div class="stat-card bg-green-50/90  px-3 sm:px-4 py-2 rounded-lg shadow-sm stats-item" style="--order: 2">
          <p class="text-xs text-gray-500 ">Found</p>
          <p class="font-bold text-lg text-green-600  animate-count">{{ getFoundDucks }}</p>
        </div>
        <div class="stat-card bg-yellow-50/90 px-3 sm:px-4 py-2 rounded-lg shadow-sm stats-item" style="--order: 3">
          <p class="text-xs text-gray-500 ">Remaining</p>
          <p class="font-bold text-lg text-yellow-600  animate-count">{{ getRemainingDucks }}</p>
        </div>
      </div>
      
      <!-- Progress bar with animated fill -->
      <div class="progress-container w-full bg-gray-200  h-3 rounded-full overflow-hidden mb-4 shadow-inner">
        <div 
          class="progress-bar h-full bg-gradient-to-r from-yellow-400 to-amber-500 progress-animation" 
          :style="{ width: `${progressPercentage}%` }"
        ></div>
      </div>
      
      <p class="text-sm text-gray-600  fade-in-animation" style="--delay: 0.7s">
        {{ getFoundDucks }} duck{{ getFoundDucks === 1 ? '' : 's' }} found, 
        <span class="font-semibold text-yellow-600 ">{{ getRemainingDucks }}</span> more to go!
      </p>
      <p class="text-xs text-gray-500  mt-1 fade-in-animation" style="--delay: 0.8s">
        New ducks will be hidden throughout your journey!
      </p>
    </div>
    
    <!-- Ducks grid with enhanced animations -->
    <div class="relative">

      <!-- Empty state with enhanced animation -->
      <div v-if="ducks.length === 0" class="bg-white/60  rounded-lg p-8 text-center fade-in-scale-animation">
        <div class="inline-flex items-center justify-center p-3 bg-yellow-100 rounded-full mb-4 animate-pulse-slow">
          <v-icon size="32" color="amber" class="animate-float">mdi-duck</v-icon>
        </div>
        <h3 class="text-xl font-semibold text-slate-800  mb-2 slide-up-animation" style="--delay: 0.2s">No Ducks Yet</h3>
        <p class="text-gray-600  max-w-md mx-auto slide-up-animation" style="--delay: 0.3s">
          There are no more ducks to find yet. Check back soon as more ducks will be hidden!
        </p>
      </div>
      
      <!-- Ducks grid display with enhanced animations -->
      <div v-else class="ducks-grid">
        <div 
          v-for="(duck, index) in ducks" 
          :key="index" 
          class="duck-item group"
          :style="{ '--delay': `${index * 0.05}s`, '--index': index }"
        >
          <div class="relative">
            <!-- Duck image with enhanced animation -->
            <div class="duck-image-wrapper">
              <img
                :src="duck.found > 0 ? getDuckImageUrl(duck) : placeHolderImage"
                class="duck-image"
                :class="{ 
                  'found': duck.found > 0, 
                  'not-found': duck.found === 0,
                }"
                :alt="duck.found > 0 ? 'Found duck' : 'Hidden duck'"
              />
              
              <!-- Enhanced hover effects for found ducks -->
              <div v-if="duck.found > 0" class="duck-overlay">
                <v-icon size="24" color="white" class="scale-animation">mdi-magnify</v-icon>
              </div>
              
              <!-- Enhanced duck status indicator -->
              <div 
                class="status-indicator" 
                :class="{ 'found': duck.found > 0, 'not-found': duck.found === 0 }"
              >
                <v-icon v-if="duck.found > 0" size="12" color="white" class="pulse-animation">mdi-check</v-icon>
                <v-icon v-else size="12" color="white" class="pulse-animation">mdi-help</v-icon>
              </div>
              
              <!-- Enhanced waves effect for found ducks -->
              <div v-if="duck.found > 0" class="wave-container">
                <div class="wave enhanced-wave"></div>
                <div class="wave enhanced-wave" style="animation-delay: 0.3s"></div>
                <div class="wave enhanced-wave" style="animation-delay: 0.6s"></div>
              </div>
            </div>
            
            <!-- Enhanced duck number -->
            <div class="duck-number appear-animation" style="--delay: calc(0.2s + (var(--index) * 0.05s))">{{ index + 1 }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDemoDuckStore } from '@/stores/demoDuckStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { getDuckImageUrl } from '@/lib/utils';

const duckStore = useDemoDuckStore();

const { getTotalDucks, getFoundDucks, getRemainingDucks, getDucksOrderedById: ducks } = storeToRefs(duckStore);

const placeHolderImage = "/ducksImages/duckPlaceholder.webp";

// Computed property for progress percentage
const progressPercentage = computed(() => {
  if (getTotalDucks.value === 0) return 0;
  return Math.round((getFoundDucks.value / getTotalDucks.value) * 100);
});
</script>

<style scoped>
.stats-section {
  animation: fadeIn 0.8s ease-out forwards;
}

/* Make stats container scroll horizontally on very small screens */
.stats-container {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  padding-bottom: 5px;
}

.stats-container::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Opera */
}

.stat-card {
  flex: 0 0 auto;
  min-width: 80px;
}

.ducks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  gap: 1rem;
  max-width: 100%;
  animation: fadeIn 0.5s ease-out forwards;
}

.duck-item {
  animation: enhancedPopIn 0.5s ease-out forwards;
  animation-delay: var(--delay);
  opacity: 0;
  transform: scale(0.5) translateY(20px);
}

.duck-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 1/1;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(45deg, rgba(255,255,255,0.5), rgba(255,255,255,0.2));
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  min-height: 55px;
  transform-style: preserve-3d;
  perspective: 1000px;
}

.duck-item:hover .duck-image-wrapper {
  transform: translateY(-8px) scale(1.05) rotateY(10deg);
  box-shadow: 0 15px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05);
}

.duck-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
}

.duck-image.found {
  filter: none;
}

.duck-image.not-found {
  filter: grayscale(100%) blur(2px);
  opacity: 0.5;
}

.duck-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.duck-item:hover .duck-overlay {
  opacity: 1;
}

.status-indicator {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-indicator.found {
  background-color: #22c55e; /* green-500 */
}

.status-indicator.not-found {
  background-color: #eab308; /* yellow-500 */
}

.duck-number {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  color: #1e293b; /* slate-800 */
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  border: 2px solid white;
}

.wave-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 30%;
  overflow: hidden;
  pointer-events: none;
  opacity: 0.6;
}

.wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%;
  height: 100%;
  background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 1200 120" xmlns="http://www.w3.org/2000/svg"><path d="M0 0v46.29c47.79 22.2 103.59 32.17 158 28 70.36-5.37 136.33-33.31 206.8-37.5 73.84-4.36 147.54 16.88 218.2 35.26 69.27 18.17 138.3 24.88 209.4 13.08 36.15-6 69.85-17.84 104.45-29.34C989.49 25 1113-14.29 1200 52.47V0z" opacity=".25" fill="%23f0b322"/><path d="M0 0v15.81c13 21.11 27.64 41.05 47.69 56.24C99.41 111.27 165 111 224.58 91.58c31.15-10.15 60.09-26.07 89.67-39.8 40.92-19 84.73-46 130.83-49.67 36.26-2.85 70.9 9.42 98.6 31.56 31.77 25.39 62.32 62 103.63 73 40.44 10.79 81.35-6.69 119.13-24.28s75.16-39 116.92-43.05c59.73-5.85 113.28 22.88 168.9 38.84 30.2 8.66 59 6.17 87.09-7.5 22.43-10.89 48-26.93 60.65-49.24V0z" opacity=".5" fill="%23f0b322"/><path d="M0 0v5.63C149.93 59 314.09 71.32 475.83 42.57c43-7.64 84.23-20.12 127.61-26.46 59-8.63 112.48 12.24 165.56 35.4C827.93 77.22 886 95.24 951.2 90c86.53-7 172.46-45.71 248.8-84.81V0z" fill="%23f0b322"/></svg>') repeat-x;
  animation: wave 10s linear infinite;
  transform: translate3d(0, 0, 0);
  background-position: 0 bottom;
  background-size: 50% auto;
}

@keyframes wave {
  0% { transform: translateX(0); }
  50% { transform: translateX(-25%); }
  100% { transform: translateX(-50%); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes popIn {
  0% { 
    opacity: 0; 
    transform: scale(0.5);
  }
  70% { 
    opacity: 1;
    transform: scale(1.05);
  }
  100% { 
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 640px) {
  .ducks-grid {
    grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
    gap: 0.75rem;
  }
  
  .duck-number {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
  
  .status-indicator {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 400px) {
  .ducks-grid {
    grid-template-columns: repeat(auto-fill, minmax(55px, 1fr));
    gap: 0.5rem;
  }
}

/* Enhanced animations */
.title-animation {
  position: relative;
  display: inline-block;
  animation: fadeIn 0.8s forwards;
  overflow: hidden;
}

.title-animation::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(to right, #eab308, #f59e0b);
  transform: translateX(-100%);
  animation: titleUnderline 1.2s 0.5s forwards;
}

@keyframes titleUnderline {
  to { transform: translateX(0); }
}

.stats-fade-in {
  opacity: 0;
  animation: fadeIn 0.8s 0.3s forwards;
}

.stats-item {
  opacity: 0;
  transform: translateY(15px);
  animation: statsItemAppear 0.5s forwards;
  animation-delay: calc(0.3s + (var(--order) * 0.1s));
}

@keyframes statsItemAppear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-count {
  display: inline-block;
  animation: countAnimation 2s forwards;
  counter-reset: count 0;
}

@keyframes countAnimation {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.progress-animation {
  position: relative;
  transform: translateX(-100%);
  animation: progressFill 1.5s 0.8s forwards cubic-bezier(0.11, 0.73, 0.4, 1);
}

@keyframes progressFill {
  to { transform: translateX(0); }
}

.fade-in-animation {
  opacity: 0;
  animation: fadeIn 0.8s forwards;
  animation-delay: var(--delay, 0s);
}

.fade-in-scale-animation {
  opacity: 0;
  transform: scale(0.9);
  animation: fadeInScale 0.6s forwards;
}

@keyframes fadeInScale {
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.slide-up-animation {
  opacity: 0;
  transform: translateY(20px);
  animation: slideUp 0.5s forwards;
  animation-delay: var(--delay, 0s);
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes enhancedPopIn {
  0% { 
    opacity: 0; 
    transform: scale(0.5) translateY(20px); 
  }
  60% { 
    opacity: 1;
    transform: scale(1.08) translateY(-5px); 
  }
  80% {
    transform: scale(0.95) translateY(2px);
  }
  100% { 
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.scale-animation {
  animation: scaleAnimation 1.5s infinite alternate;
}

@keyframes scaleAnimation {
  from { transform: scale(1); }
  to { transform: scale(1.15); }
}

.pulse-animation {
  animation: pulseAnimation 2s infinite;
}

@keyframes pulseAnimation {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.enhanced-wave {
  filter: hue-rotate(0deg);
  animation: waveAndHue 10s linear infinite;
}

@keyframes waveAndHue {
  0% { transform: translateX(0); filter: hue-rotate(0deg); }
  50% { transform: translateX(-25%); filter: hue-rotate(20deg); }
  100% { transform: translateX(-50%); filter: hue-rotate(0deg); }
}

.appear-animation {
  opacity: 0;
  transform: scale(0) translateY(10px);
  animation: appearAnimation 0.4s forwards;
  animation-delay: var(--delay);
}

@keyframes appearAnimation {
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Handling reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  .title-animation::after,
  .stats-fade-in,
  .stats-item,
  .animate-count,
  .progress-animation,
  .fade-in-animation,
  .fade-in-scale-animation,
  .slide-up-animation,
  .duck-item,
  .scale-animation,
  .pulse-animation,
  .enhanced-wave,
  .appear-animation {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
}

/* Enhanced mobile responsiveness */
@media (max-width: 640px) {
  .duck-item:hover .duck-image-wrapper {
    transform: translateY(-5px) scale(1.03);
  }
}
</style>